import type {
  PackageBillingDimension,
  PackageConfig,
  PricingGridsResponse,
} from "@itaas/pep-contracts";

export type BillingDimensionKey =
  | "users"
  | "beds"
  | "patients"
  | "students"
  | "sites"
  | "terminals"
  | "endpoints"
  | "guards"
  | "staff"
  | "employees"
  | "warehouses"
  | "invoices"
  | "skus"
  | "entities"
  | "scanPoints"
  | "included";

const PLATFORM_MODULES = new Set(["iam", "suite", "notifications"]);

const DEFAULT_LABELS_FR: Record<string, string> = {
  beds: "Lits hospitaliers",
  patients: "Patients / jour",
  users: "Utilisateurs",
  staff: "Personnel",
  employees: "Employés",
  students: "Élèves inscrits",
  sites: "Sites",
  warehouses: "Magasins / dépôts",
  invoices: "Factures",
  skus: "Références actives",
  entities: "Entités",
  scanPoints: "Points de scan",
  endpoints: "Points de scan",
  terminals: "Terminaux",
  guards: "Agents",
};

const DEFAULT_LABELS_EN: Record<string, string> = {
  beds: "Hospital beds",
  patients: "Patients / day",
  users: "Users",
  staff: "Staff",
  employees: "Employees",
  students: "Enrolled students",
  sites: "Sites",
  warehouses: "Warehouses",
  invoices: "Invoices",
  skus: "Active SKUs",
  entities: "Entities",
  scanPoints: "Scan points",
  endpoints: "Scan endpoints",
  terminals: "Terminals",
  guards: "Guards",
};

const DEFAULT_QTY: Record<string, number> = {
  beds: 10,
  patients: 25,
  users: 5,
  staff: 10,
  employees: 30,
  students: 50,
  sites: 1,
  warehouses: 1,
  invoices: 100,
  skus: 100,
  entities: 1,
  scanPoints: 2,
  endpoints: 3,
  terminals: 2,
  guards: 2,
};

const DEFAULT_CONFIRM_BELOW: Record<string, { qty: number; fr: string; en: string }> = {
  beds: {
    qty: 5,
    fr: "Votre établissement ne dispose que de {{qty}} lit(s) hospitalier(s) ?",
    en: "Your facility only has {{qty}} hospital bed(s)?",
  },
  users: {
    qty: 3,
    fr: "Êtes-vous sûr que votre organisation ne compte que {{qty}} utilisateur(s) back-office ?",
    en: "Are you sure your organization only has {{qty}} back-office user(s)?",
  },
  students: {
    qty: 20,
    fr: "Votre établissement n'inscrit que {{qty}} élève(s) ?",
    en: "Your school only enrolls {{qty}} student(s)?",
  },
  sites: {
    qty: 1,
    fr: "Vous exploitez un seul site minier ?",
    en: "You operate only one mining site?",
  },
  endpoints: {
    qty: 2,
    fr: "Seulement {{qty}} point(s) de scan pour votre site ?",
    en: "Only {{qty}} scan endpoint(s) for your site?",
  },
  terminals: {
    qty: 2,
    fr: "Seulement {{qty}} terminal(aux) biométrique(s) ?",
    en: "Only {{qty}} biometric terminal(s)?",
  },
};

export function dimensionKeyFromPricingUnit(unit: string): BillingDimensionKey {
  if (unit.includes("included")) return "included";
  if (unit.includes("bed")) return "beds";
  if (unit.includes("patient")) return "patients";
  if (unit.includes("employee")) return "employees";
  if (unit.includes("student")) return "students";
  if (unit.includes("site")) return "sites";
  if (unit.includes("warehouse")) return "warehouses";
  if (unit.includes("invoice")) return "invoices";
  if (unit.includes("sku")) return "skus";
  if (unit.includes("entity")) return "entities";
  if (unit.includes("terminal")) return "terminals";
  if (unit.includes("scan-point")) return "scanPoints";
  if (unit.includes("endpoint")) return "endpoints";
  if (unit.includes("guard")) return "guards";
  return "users";
}

export function moduleDimensionKey(
  moduleSlug: string,
  grids: PricingGridsResponse,
): BillingDimensionKey {
  const unit = grids.products[moduleSlug]?.unit ?? "user/month";
  return dimensionKeyFromPricingUnit(unit);
}

export function initialQuantitiesForPackage(pkg: PackageConfig): Record<string, number> {
  const quantities: Record<string, number> = {};
  for (const dim of pkg.billingDimensions ?? []) {
    quantities[dim.key] = dim.defaultQty;
  }
  if (!Object.keys(quantities).length) {
    quantities.users = pkg.defaultSeats ?? DEFAULT_QTY.users;
  }
  return quantities;
}

export function primarySeatCount(quantities: Record<string, number>): number {
  return quantities.users ?? Object.values(quantities)[0] ?? 1;
}

/** Dimensions du package ayant au moins un module sélectionné concerné. */
export function activeBillingDimensions(
  pkg: PackageConfig,
  selectedModules: string[],
): PackageBillingDimension[] {
  const billable = selectedModules.filter((s) => !PLATFORM_MODULES.has(s));
  return (pkg.billingDimensions ?? []).filter((dim) =>
    dim.moduleSlugs.some((slug) => billable.includes(slug)),
  );
}

function deriveBillingDimensionsFromGrids(
  selectedModules: string[],
  grids: PricingGridsResponse,
): PackageBillingDimension[] {
  const billable = selectedModules.filter((s) => !PLATFORM_MODULES.has(s));
  const byKey = new Map<string, { slugs: string[]; unit: string }>();

  for (const slug of billable) {
    const row = grids.products[slug];
    const unit = row?.unit ?? "user/month";
    if (!row || unit.includes("included")) continue;
    const key = dimensionKeyFromPricingUnit(unit);
    if (key === "included") continue;
    const entry = byKey.get(key) ?? { slugs: [], unit };
    entry.slugs.push(slug);
    byKey.set(key, entry);
  }

  return [...byKey.entries()].map(([key, { slugs }]) => {
    const confirm = DEFAULT_CONFIRM_BELOW[key];
    return {
      key,
      labelFr: DEFAULT_LABELS_FR[key] ?? key,
      labelEn: DEFAULT_LABELS_EN[key] ?? key,
      defaultQty: DEFAULT_QTY[key] ?? 5,
      minQty: 1,
      confirmBelowQty: confirm?.qty,
      confirmMessageFr: confirm?.fr,
      confirmMessageEn: confirm?.en,
      moduleSlugs: slugs,
    };
  });
}

function legacyBillingDimensions(
  pkg: PackageConfig,
  selectedModules: string[],
): PackageBillingDimension[] {
  const billable = selectedModules.filter((s) => !PLATFORM_MODULES.has(s));
  if (!billable.length) return [];
  return [
    {
      key: "users",
      labelFr: pkg.seatLabelFr ?? "Quantité (lits / utilisateurs)",
      labelEn: pkg.seatLabelEn ?? "Quantity (beds / users)",
      defaultQty: pkg.defaultSeats ?? DEFAULT_QTY.users,
      minQty: 1,
      confirmBelowQty: DEFAULT_CONFIRM_BELOW.users.qty,
      confirmMessageFr: DEFAULT_CONFIRM_BELOW.users.fr,
      confirmMessageEn: DEFAULT_CONFIRM_BELOW.users.en,
      moduleSlugs: billable,
    },
  ];
}

/** Dimensions actives avec repli si l'API renvoie encore l'ancien format. */
export function resolveBillingDimensions(
  pkg: PackageConfig,
  selectedModules: string[],
  grids?: PricingGridsResponse | null,
): PackageBillingDimension[] {
  const configured = activeBillingDimensions(pkg, selectedModules);
  if (configured.length > 0) return configured;
  if (grids) {
    const derived = deriveBillingDimensionsFromGrids(selectedModules, grids);
    if (derived.length > 0) return derived;
  }
  return legacyBillingDimensions(pkg, selectedModules);
}

export function dimensionMinQty(dim: PackageBillingDimension): number {
  return Math.max(1, dim.minQty ?? 1);
}

export function needsLowQuantityConfirmation(
  dim: PackageBillingDimension,
  value: number,
  confirmedKeys: ReadonlySet<string>,
): boolean {
  if (confirmedKeys.has(dim.key)) return false;
  const threshold = dim.confirmBelowQty;
  if (threshold == null) return false;
  return value < threshold && value >= dimensionMinQty(dim);
}

export function lowQuantityMessage(
  dim: PackageBillingDimension,
  value: number,
  locale: "fr" | "en",
): string {
  const template =
    locale === "en"
      ? dim.confirmMessageEn ?? DEFAULT_CONFIRM_BELOW[dim.key]?.en
      : dim.confirmMessageFr ?? DEFAULT_CONFIRM_BELOW[dim.key]?.fr;
  if (template) return template.replace(/\{\{qty\}\}/g, String(value));
  return locale === "en"
    ? `Only ${value} — is that correct for your organization?`
    : `Seulement ${value} — est-ce bien le volume de votre organisation ?`;
}

/**
 * Associe chaque métrique à un module « ancrage » pour l'affichage inline
 * (une seule saisie par clé, au sein de la carte module concernée).
 */
export function billingDimensionAnchors(
  pkg: PackageConfig,
  selectedModules: string[],
): Map<string, PackageBillingDimension> {
  const anchors = new Map<string, PackageBillingDimension>();
  const usedKeys = new Set<string>();

  for (const dim of activeBillingDimensions(pkg, selectedModules)) {
    if (usedKeys.has(dim.key)) continue;
    const anchor = dim.moduleSlugs.find((slug) => selectedModules.includes(slug));
    if (anchor) {
      anchors.set(anchor, dim);
      usedKeys.add(dim.key);
    }
  }
  return anchors;
}

export function quantityForModule(
  moduleSlug: string,
  quantities: Record<string, number>,
  grids: PricingGridsResponse,
  pkg: PackageConfig,
): number {
  const dimKey = moduleDimensionKey(moduleSlug, grids);
  const explicit = pkg.billingDimensions?.find(
    (d) => d.key === dimKey && d.moduleSlugs.includes(moduleSlug),
  );
  if (explicit) return Math.max(1, quantities[explicit.key] ?? explicit.defaultQty);

  const fallbackDim = pkg.billingDimensions?.find((d) => d.moduleSlugs.includes(moduleSlug));
  if (fallbackDim) return Math.max(1, quantities[fallbackDim.key] ?? fallbackDim.defaultQty);

  return Math.max(1, quantities[dimKey] ?? quantities.users ?? 1);
}

export function formatPricingUnitLabel(unit: string, locale: "fr" | "en"): string {
  const mapFr: Record<string, string> = {
    "user/month": "utilisateur",
    "bed/month": "lit",
    "patient/day": "patient/jour",
    "employee/month": "employé",
    "student/month": "élève",
    "site/month": "site",
    "warehouse/month": "magasin",
    "invoice/month": "facture",
    "sku/month": "référence",
    "entity/month": "entité",
    "scan-point/month": "point de scan",
    "terminal/month": "terminal",
    "endpoint/month": "point de scan",
    "guard/month": "agent",
  };
  const mapEn: Record<string, string> = {
    "user/month": "user",
    "bed/month": "bed",
    "patient/day": "patient/day",
    "employee/month": "employee",
    "student/month": "student",
    "site/month": "site",
    "warehouse/month": "warehouse",
    "invoice/month": "invoice",
    "sku/month": "SKU",
    "entity/month": "entity",
    "scan-point/month": "scan point",
    "terminal/month": "terminal",
    "endpoint/month": "endpoint",
    "guard/month": "guard",
  };
  const map = locale === "en" ? mapEn : mapFr;
  return map[unit] ?? unit.split("/")[0];
}

export function dimensionLabel(
  dim: PackageBillingDimension,
  locale: "fr" | "en",
): string {
  return locale === "en" ? dim.labelEn ?? dim.labelFr : dim.labelFr;
}

export function dimensionHint(
  dim: PackageBillingDimension,
  locale: "fr" | "en",
): string | undefined {
  return locale === "en" ? dim.hintEn ?? dim.hintFr : dim.hintFr;
}
